/**
 * Wrap-rate / billing-rate visibility — role-controlled per requirements
 * list ("Role-controlled wrap rate and billing rate visibility").
 *
 * Pure helpers. The role check itself is intentionally simple — moonshot
 * has its own ABAC layer that will eventually own this. Today we use a
 * coarse role-name allowlist matching what the standalone shipped:
 * managers, contracts, and finance roles see wrap; everyone else gets
 * the unburdened rate.
 *
 * computeFullyBurdenedRate falls back through standardHourlyRate /
 * weeklySalary÷40, same as payRules.
 */
import type { TsEmployee } from '../types/timesheet.types'

/**
 * Roles permitted to see wrap-multiplied (fully-burdened) hourly /
 * weekly figures. Anything else gets the raw rate.
 */
const WRAP_PERMITTED_ROLES: ReadonlySet<string> = new Set([
  'PortfolioManager',
  'ProgramManager',
  'Contracts',
  'ContractsAdmin',
  'Finance',
  'FinanceAdmin',
  'GlobalAdmin',
  'OrgAdmin',
])

export function canSeeWrapRate(viewerRole: string | undefined | null): boolean {
  if (!viewerRole) return false
  return WRAP_PERMITTED_ROLES.has(viewerRole)
}

/**
 * Returns the base hourly rate (no wrap). Falls back to weeklySalary/40
 * when standardHourlyRate isn't set.
 */
export function baseHourlyRate(employee: TsEmployee): number {
  if (employee.standardHourlyRate && employee.standardHourlyRate > 0) return employee.standardHourlyRate
  if (employee.weeklySalary && employee.weeklySalary > 0) return employee.weeklySalary / 40
  return 0
}

/**
 * Returns the fully-burdened (wrap-multiplied) hourly rate when the
 * viewer is permitted to see it; otherwise returns null. Caller renders
 * "—" or "(restricted)" for null.
 */
export function fullyBurdenedRate(employee: TsEmployee, viewerRole: string | undefined | null): number | null {
  if (!canSeeWrapRate(viewerRole)) return null
  const base = baseHourlyRate(employee)
  if (base === 0) return null
  const wrap = employee.wrapRate ?? 1
  return Math.round(base * wrap * 100) / 100
}
